<template>
  <div class="loading">
    <div class="loading__title-container">
      <Heading
        v-for="text in texts"
        :key="text"
        class="loading__title"
        :class="{
          'loading__title--active': currentText === text,
          'loading__title--entering': currentText === text && isEntering,
          'loading__title--leaving': previousText === text
        }"
        :level="2"
      >
        {{ text }}
      </Heading>
    </div>
    <ProgressBar
      class="loading__progressbar"
      :progress="progress"
      :progress-color="NebraskaColors.brandPrimary"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, inject, Ref, watch, onMounted } from 'vue';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { Heading, ProgressBar } from '@warrenbrasil/nebraska-web';
import { EVariableIncomeAcquisitionSteps } from '../../helpers';

const isCreatingPortfolio = inject('isCreatingPortfolio') as Ref<boolean>;
const currentStep = inject(
  'currentStep'
) as Ref<EVariableIncomeAcquisitionSteps>;
const progress = ref(0);
const isEntering = ref(false);
const previousText = ref('');
let initialAnimationInterval: NodeJS.Timeout;

const texts = ['Criando carteira...', 'Solicitando investimento...', 'Pronto!'];

const currentText = computed(() => {
  if (progress.value >= 0.75) return texts[2];
  if (progress.value >= 0.4) return texts[1];
  if (progress.value >= 0.1) return texts[0];

  return '';
});

watch(currentText, (_newText, oldText) => {
  previousText.value = oldText;
  isEntering.value = true;
  setTimeout(() => {
    isEntering.value = false;
  }, 400);
});

const animateProgress = (targetValue: number, duration: number) => {
  const startValue = progress.value;
  const steps = 60;
  const stepDuration = duration / steps;
  let currentStep = 0;

  const interval = setInterval(() => {
    currentStep++;
    const currentProgress = currentStep / steps;

    if (currentStep >= steps) {
      progress.value = targetValue;
      clearInterval(interval);
      return;
    }

    progress.value = startValue + (targetValue - startValue) * currentProgress;
  }, stepDuration);

  return interval;
};

onMounted(() => {
  initialAnimationInterval = animateProgress(0.6, 2500);
});

watch(isCreatingPortfolio, newValue => {
  if (!newValue) {
    clearTimeout(initialAnimationInterval);

    animateProgress(1, 2000);

    setTimeout(() => {
      currentStep.value = EVariableIncomeAcquisitionSteps.CONCLUSION;
    }, 2500);
  }
});
</script>

<style lang="less" scoped>
@navbar-height: 64px;

.loading {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  height: calc(100vh - @navbar-height);
  padding: @size-spacing-x400 @size-spacing-x400 @size-spacing-x1400
    @size-spacing-x400;

  &__title-container {
    width: 100%;
    height: 48px;
    margin-bottom: @size-spacing-x900;
    position: relative;
    overflow: visible;
  }

  &__title {
    position: absolute;
    width: 100%;
    text-align: center;
    opacity: 0;

    &--active {
      opacity: 1;
    }

    &--entering {
      animation: fadeIn 0.4s ease-in-out forwards;
    }

    &--leaving {
      animation: fadeOut 0.6s ease-in-out forwards;
    }
  }

  &__progressbar {
    width: 100%;
  }
}

@keyframes fadeIn {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

@keyframes fadeOut {
  0% {
    opacity: 1;
    transform: translateY(0);
  }
  100% {
    opacity: 0;
    transform: translateY(-50px);
  }
}

:deep(.progressbar--status) {
  transition: all 0.2s linear;
}
</style>
